import { useSelector } from "react-redux"
import { useInscriptionFunnel } from "../context/InscriptionFunnelContext"
import { FORMAT_STEP } from "../constants/inscriptionFormats"
import { inscriptionFunnelService } from "../services/inscriptionFunnelService"

/**
 * L'identité du lead, lue au Context PUIS au store.
 *
 * Le Context est alimenté par un callback `useApiStatus` monté sur l'écran de
 * départ. Or la classe arrive pré-remplie depuis la landing : le parent clique
 * « Continuer » en moins d'une seconde, l'écran se démonte avant la réponse de
 * `start`, et le callback ne s'exécute jamais. Le parcours continuait alors
 * SANS identifiant : les écrans répondaient aux clics, mais plus rien n'était
 * écrit côté serveur — donc aucun devis, et un paiement impossible à ouvrir.
 * C'est le chemin NOMINAL qui tombait dedans, pas un cas limite.
 *
 * Le store, lui, est alimenté par le middleware api quel que soit l'écran
 * monté : il survit au démontage. Il fait donc le repli, et le Context reste
 * prioritaire (il porte la valeur la plus fraîche après une reprise).
 */
export const useInscriptionLead = () => {
    const { state } = useInscriptionFunnel()
    const store = useSelector((s) => s.data?.inscriptionFunnel)

    const creating = useSelector(
        (s) =>
            s.api?.[inscriptionFunnelService.startInscriptionFunnel.name]
                ?.request ||
            s.api?.[inscriptionFunnelService.initInscriptionFromHubSpot.name]
                ?.request ||
            s.api?.[inscriptionFunnelService.resumeInscription.name]?.request
    )

    const funnelStateId = state.funnelStateId || store?.funnelStateId || null
    const parentEmail = state.parentEmail || store?.initUser?.email || ""

    return {
        funnelStateId,
        parentEmail,
        // Où ce dossier choisit son format — décidé par le serveur. Comme
        // l'identifiant : le Context d'abord (il est par onglet), le store en
        // repli quand l'écran de départ s'est démonté avant la réponse.
        formatStep:
            state.formatStep || store?.formatStep || FORMAT_STEP.samePage,
        // Une entrée est EN COURS de création : ni identifiant, ni échec — il
        // faut attendre. Sans ça, une garde qui teste l'identifiant renvoie au
        // début du parcours pendant l'aller-retour, c'est-à-dire exactement sur
        // le chemin nominal (la classe arrive pré-remplie, le parent clique en
        // moins d'une seconde).
        isResolvingLead: !!creating,
        // Jeton d'identité attendu par les routes derrière `auth_or_email`.
        userToken:
            parentEmail && typeof btoa === "function"
                ? btoa(parentEmail)
                : null,
    }
}
